import classNames from 'classnames'
import React from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { getScopeAccessType, getScopeDescription, type ScopeAccessType } from '../../../common/scopeCategories'
import { CONTACT_REQUIRED_SCOPE_IDS, INTEGRATIONS_CONTACT_EMAIL } from '../constants'
import './OAuth2AppsPermissionsTab.scss'

interface PermissionAccessBadgeProps {
  access: ScopeAccessType
  dimmed?: boolean
}

const PermissionAccessBadge: React.FC<PermissionAccessBadgeProps> = ({ access, dimmed }) => (
  <div
    className={classNames('d-flex py-1 px-2 justify-content-center align-items-center zocks-gap-0.25 zocks-border-0.5', {
      'zocks-text-gray-600 zocks-bg-color-gray-200': dimmed,
      'zocks-text-primary-dark zocks-bg-color-primary-light': !dimmed && access === 'read',
      'zocks-text-warning-text zocks-bg-color-warning': !dimmed && access !== 'read',
    })}
  >
    {access === 'read' ? 'Read' : 'Write'}
  </div>
)

const TranscriptApprovalBanner: React.FC = () => (
  <div className='d-flex align-items-center zocks-gap-2 p-3 mx-3 mb-3 rounded-3 border zocks-border-color-gray-300 zocks-bg-color-gray-100'>
    <i className='bi bi-lock zocks-text-gray-900' />
    <div className='zocks-text-gray-900'>
      <span className='fw-bold'>Transcript access requires approval.</span> To add transcript access to your app, email us at{' '}
      <a href={`mailto:${INTEGRATIONS_CONTACT_EMAIL}`} className='zocks-text-gray-900 text-decoration-underline'>
        {INTEGRATIONS_CONTACT_EMAIL}
      </a>
      .
    </div>
  </div>
)

interface OAuth2AppsPermissionRowProps {
  scope: string
  description: string
  isEditMode?: boolean
  isChecked?: boolean
  onToggle?: (scope: string) => void
}

export const OAuth2AppsPermissionRow: React.FC<OAuth2AppsPermissionRowProps> = ({ scope, description, isEditMode, isChecked, onToggle }) => {
  const isUnchecked = !isChecked
  const label = getScopeDescription(scope, description)
  // Some scopes can only be granted with manual approval from the integrations team. While not yet
  // granted, the checkbox is locked and an approval banner is shown. Once granted, the user can
  // toggle (and remove) it themselves. Derived from the scope id so every usage stays consistent.
  const requiresApproval = CONTACT_REQUIRED_SCOPE_IDS.includes(scope)
  const isBlockedPendingApproval = requiresApproval && isUnchecked
  const showApprovalBanner = !!isEditMode && isBlockedPendingApproval

  return (
    <div
      className={classNames('apps-permissions-row-box mx-0 my-3', {
        'apps-permissions-row-box--edit': isEditMode,
        'apps-permissions-row-box--unchecked': isUnchecked,
      })}
    >
      <Row className='py-3 px-4 mx-0 zocks-gap-2 align-items-center'>
        {isEditMode ? (
          <Col xs='auto' className='p-0 scope-checkbox-col'>
            <Form.Check
              type='checkbox'
              id={`scope-checkbox-${scope}`}
              checked={isChecked}
              disabled={isBlockedPendingApproval}
              onChange={() => onToggle?.(scope)}
              aria-label={label}
            />
          </Col>
        ) : null}
        <Col xs={9} className='d-flex flex-column gap-1 p-0'>
          <div className={classNames('h6 mb-0', { 'zocks-text-gray-600': isUnchecked })}>{label}</div>
          <div className={classNames('label-medium', isUnchecked ? 'zocks-text-gray-500' : 'zocks-text-gray-600')}>{scope}</div>
        </Col>
        <Col className='d-flex flex-column justify-content-center align-items-end p-0'>
          <PermissionAccessBadge access={getScopeAccessType(scope)} dimmed={isUnchecked} />
        </Col>
      </Row>
      {showApprovalBanner ? <TranscriptApprovalBanner /> : null}
    </div>
  )
}
